import { MapPinIcon } from "@heroicons/react/20/solid";
import { DescriptionItem, DescriptionList } from "../components/DescriptionList";
import { OtsTooltip } from "../components/OtsTooltip";
import { formatActivityDuration, formatActivityTime } from "./driver-activity-format";
import type { ActivityGeofenceMatch } from "./driver-activity-view-model";

export type ActivityGeofenceChipProps = {
  /** Missing when no geofence visit was matched to the stop. */
  match?: ActivityGeofenceMatch;
  /** When the ticket was written, to show how long after arriving. */
  ticketTime?: number;
  timeZone?: string;
};

const states: Record<ActivityGeofenceMatch["state"], { label: string; classes: string }> = {
  matched: { label: "Geofence matched", classes: "bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)]" },
  overridden: { label: "Geofence overridden", classes: "bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]" },
  "outside-shift": { label: "Geofence visit ran past the end of the shift", classes: "bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]" },
};

/**
 * Whether a stop has a matched geofence visit, and how long the truck was there. Green matched, amber
 * overridden by hand, red when the visit ran past the shift; a hollow pin when nothing matched.
 */
export const ActivityGeofenceChip = ({ match, ticketTime, timeZone }: ActivityGeofenceChipProps) => {
  if (!match) {
    return (
      <OtsTooltip content="No geofence visit matched to this stop" density="compact">
        <span tabIndex={0} role="img" aria-label="No geofence matched" data-geofence="none" className="inline-flex text-[var(--ots-outline)]">
          <MapPinIcon aria-hidden="true" className="size-3.5 opacity-60"/>
        </span>
      </OtsTooltip>
    );
  }

  const { event } = match;
  const state = states[match.state];
  const dwell = event.durationInMillis ?? (event.outDttm != null ? event.outDttm - event.inDttm : undefined);
  const lead = ticketTime != null ? ticketTime - event.inDttm : undefined;

  return (
    <OtsTooltip
      density="compact"
      content={<>
        <p className="mb-1 font-semibold">{state.label}</p>
        <DescriptionList layout="inline" tone="inherit" compact>
          {event.siteName && <DescriptionItem label="Site">{event.siteName}</DescriptionItem>}
          <DescriptionItem label="In">{formatActivityTime(event.inDttm, timeZone)}</DescriptionItem>
          <DescriptionItem label="Out">{event.outDttm != null ? formatActivityTime(event.outDttm, timeZone) : "still on site"}</DescriptionItem>
          {dwell != null && <DescriptionItem label="On site">{formatActivityDuration(dwell)}</DescriptionItem>}
          {lead != null && lead >= 0 && <DescriptionItem label="Ticket written">{formatActivityDuration(lead)} after arriving</DescriptionItem>}
        </DescriptionList>
      </>}>
      <span
        tabIndex={0}
        role="img"
        aria-label={`${state.label}${dwell != null ? `, ${formatActivityDuration(dwell)} on site` : ""}`}
        data-geofence={match.state}
        className={["inline-flex items-center gap-0.5 rounded-full py-px pl-0.5 pr-1.5 text-[10px] font-semibold leading-4 tabular-nums", state.classes].join(" ")}>
        <MapPinIcon aria-hidden="true" className="size-3"/>
        {dwell != null ? formatActivityDuration(dwell) : "on site"}
      </span>
    </OtsTooltip>
  );
};
